/**
 * Loop — the frame. Fixed-timestep accumulator with render interpolation, a spiral-of-death
 * guard, a scalable clock for hitstop/slow-mo, and per-frame timing for the debug overlay.
 */

/** spiral-of-death guard: past this the loop drops the backlog instead of chasing it */
const MAX_STEPS = 5;
/** a frame longer than this is a stall (debugger, tab thrash) and is not simulated */
const MAX_FRAME_DT = 0.25;
const HISTORY = 180;
/** EMA retention for the smoothed readouts — high enough that the numbers are readable */
const EMA = 0.9;
const NOMINAL_MS = 1000 / 60;

export interface LoopCallbacks {
  /** Called 0..MAX_STEPS times per frame with a constant dt. */
  fixedUpdate(dt: number, tick: number): void;
  /** Called exactly once per frame. alpha = accumulator / fixedDt, in [0,1). */
  render(alpha: number, frameDt: number, elapsed: number): void;
  /** Called once per frame BEFORE any fixedUpdate. Use for input polling. */
  beginFrame?(frameDt: number): void;
  /** Called once per frame AFTER render. Use for clearing input edges. */
  endFrame?(frameDt: number): void;
}

export class Loop {
  readonly fixedDt: number;
  /** simulated seconds; drives every shader clock, so hitstop freezes the picture too */
  elapsed = 0;

  private readonly cb: LoopCallbacks;
  private _timeScale = 1;
  private _tick = 0;
  private accumulator = 0;
  private last = 0;
  private rafId = 0;
  private running = false;
  /** set after any wall-clock discontinuity (start, tab return) to rebase `last` */
  private resync = true;

  private _fps = 60;
  private _frameMs = NOMINAL_MS;
  private _rawFrameMs = NOMINAL_MS;
  private _updateMs = 0;
  private _renderMs = 0;
  private _steps = 0;
  private readonly _history = new Float32Array(HISTORY).fill(NOMINAL_MS);
  private _historyHead = 0;

  constructor(cb: LoopCallbacks, fixedHz = 60) {
    this.cb = cb;
    this.fixedDt = 1 / (fixedHz > 0 ? fixedHz : 60);
  }

  get timeScale(): number {
    return this._timeScale;
  }

  set timeScale(v: number) {
    this._timeScale = Number.isFinite(v) ? (v < 0 ? 0 : v > 4 ? 4 : v) : 1;
  }

  get tick(): number {
    return this._tick;
  }

  get fps(): number {
    return this._fps;
  }

  get frameMs(): number {
    return this._frameMs;
  }

  get rawFrameMs(): number {
    return this._rawFrameMs;
  }

  get updateMs(): number {
    return this._updateMs;
  }

  get renderMs(): number {
    return this._renderMs;
  }

  get steps(): number {
    return this._steps;
  }

  get history(): Float32Array {
    return this._history;
  }

  /** index of the next slot to be written — i.e. the OLDEST sample. Walk head → head+180. */
  get historyHead(): number {
    return this._historyHead;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.resync = true;
    this.last = performance.now();
    document.addEventListener('visibilitychange', this.onVisibility);
    this.rafId = requestAnimationFrame(this.frame);
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    cancelAnimationFrame(this.rafId);
    this.rafId = 0;
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  private onVisibility = (): void => {
    // rAF is parked while hidden; without this the first visible frame tries to
    // simulate the whole background interval at once.
    this.accumulator = 0;
    this.resync = true;
  };

  private frame = (now: number): void => {
    this.rafId = requestAnimationFrame(this.frame);

    let rawMs = now - this.last;
    this.last = now;
    if (this.resync) {
      this.resync = false;
      rawMs = this.fixedDt * 1000;
    } else if (rawMs < 0) {
      rawMs = 0;
    }

    this._rawFrameMs = rawMs;
    this._history[this._historyHead] = rawMs;
    this._historyHead = (this._historyHead + 1) % HISTORY;
    // 0.5 ms floor stops a coalesced 0 ms frame from spiking the average to 5-digit fps
    this._fps = this._fps * EMA + (1000 / Math.max(rawMs, 0.5)) * (1 - EMA);
    this._frameMs = this._frameMs * EMA + rawMs * (1 - EMA);

    const dt = Math.min(rawMs * 0.001, MAX_FRAME_DT);

    const tBegin = performance.now();
    this.cb.beginFrame?.(dt);

    // Scaling the accumulator (not the step dt) is what makes hitstop real: fewer fixed
    // steps per wall-clock second, every system still stepping at its constant dt.
    this.accumulator += dt * this._timeScale;
    let steps = 0;
    while (this.accumulator >= this.fixedDt && steps < MAX_STEPS) {
      this.accumulator -= this.fixedDt;
      this._tick++;
      this.cb.fixedUpdate(this.fixedDt, this._tick);
      this.elapsed += this.fixedDt;
      steps++;
    }
    if (this.accumulator >= this.fixedDt) {
      // hit the cap: keep the sub-step phase, throw away the debt so alpha stays in [0,1)
      this.accumulator %= this.fixedDt;
    }
    this._steps = steps;

    const tRender = performance.now();
    this._updateMs = this._updateMs * EMA + (tRender - tBegin) * (1 - EMA);

    this.cb.render(this.accumulator / this.fixedDt, dt, this.elapsed);

    this._renderMs = this._renderMs * EMA + (performance.now() - tRender) * (1 - EMA);
    this.cb.endFrame?.(dt);
  };
}
